/* eslint-disable no-case-declarations */

import { useCallback, useEffect, useState } from 'react';
import { unparse } from 'papaparse';
import { useMessage } from '@tmapy/intl';
import { ValuesOf } from '@tmapy/beetlejuice-utils';

import { SecondaryBtn } from '@tmapy/style-guide';
import { QueryState } from '@tmapy/style-guide/dist/constants';

import { CHECK_STATE, checkSourceEnum, DOC_STATE, HISTORY_CLOSED } from '../constants';
import { getDocumentStates } from './DocList';

import { checkListMessages, docListMessages, prejimkaTypesMessages } from './docsMessages';
import { checkSourceMessages, checkStateMessages } from './checkMessages';
import { formatDocState } from '../utils/formatDocState';

import { docsList } from '../../openapi/operations/docsList';

export const ExportMappingType = {
  DEFAULT: 'DEFAULT',
  CHECK: 'CHECK',
  DMVS_DTI: 'DMVS_DTI',
  DMVS_GAD: 'DMVS_GAD',
  DMVS_CROSS: 'DMVS_CROSS',
  DTMK_DTI: 'DTMK_DTI',
  DTMK_KAD: 'DTMK_KAD',
} as const;
export type ExportMappingTypeEnum = ValuesOf<typeof ExportMappingType>;

const MAPPING: Record<
  ExportMappingTypeEnum,
  Record<string, { id: string; defaultMessage: string }>
> = {
  DEFAULT: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    created: docListMessages.docListColumnCreated,
    history: docListMessages.docListColumnClosed,
    dmvs_id_podani: docListMessages.docListColumnDmvsIdPodani,
    dmvs_source: docListMessages.docListColumnDmvsSource,
  },
  DMVS_DTI: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    created: docListMessages.docListColumnCreated,
    history: docListMessages.docListColumnClosed,
    dmvs_id_podani: docListMessages.docListColumnDmvsIdPodani,
    dmvs_source: docListMessages.docListColumnDmvsSource,
    subjekt: docListMessages.docListColumnSubjekt,
    editor_subjekt: docListMessages.docListColumnEditorManager,
  },
  DMVS_GAD: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    created: docListMessages.docListColumnCreated,
    history: docListMessages.docListColumnClosed,
    dmvs_id_podani: docListMessages.docListColumnDmvsIdPodani,
    dmvs_source: docListMessages.docListColumnDmvsSource,
    subjekt: docListMessages.docListColumnSubjekt,
    geodesist: docListMessages.docListColumnGeodesist,
    editor_manager: docListMessages.docListColumnEditorManager,
  },
  DMVS_CROSS: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    svu_cross_names: docListMessages.docListColumnSvu,
    created: docListMessages.docListColumnCreated,
    history: docListMessages.docListColumnClosed,
    dmvs_id_podani: docListMessages.docListColumnDmvsIdPodani,
    dmvs_source: docListMessages.docListColumnDmvsSource,
    subjekt: docListMessages.docListColumnSubjekt,
    geodesist: docListMessages.docListColumnGeodesist,
    editor_manager: docListMessages.docListColumnEditorManager,
  },
  DTMK_DTI: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    created: docListMessages.docListColumnDtmkCreated,
    history: docListMessages.docListColumnClosed,
    creator: docListMessages.docListColumnUser,
    geodesist: docListMessages.docListColumnGeodesist,
    editor_manager: docListMessages.docListColumnEditorManager,
  },
  DTMK_KAD: {
    id: docListMessages.docListColumnNumber,
    state: docListMessages.docListColumnState,
    name: docListMessages.docListColumnName,
    description: docListMessages.docListColumnDescription,
    prejimka_type: docListMessages.docListColumnPrejimkaType,
    created: docListMessages.docListColumnDtmkCreated,
    history: docListMessages.docListColumnClosed,
    creator: docListMessages.docListColumnAuthor,
    geodesist: docListMessages.docListColumnGeodesist,
  },
  CHECK: {
    id: checkListMessages.checkListColumnNumber,
    latest_check: checkListMessages.checkListColumnState,
    name: checkListMessages.checkListColumnName,
    dmvs_source: checkListMessages.checkListColumnCheckSource,
    dmvs_id_podani: checkListMessages.checkListColumnDmvsIdPodani,
    created: checkListMessages.checkListColumnCreated,
    creator: checkListMessages.checkListColumnAuthor,
    geodesist: checkListMessages.checkListColumnGeodesist,
  },
};

/**
 * https://github.com/mholt/PapaParse
 * fastest in-browser CSV (or delimited text) parser for JavaScript
 */
const csvUnparseConfig = {
  quotes: false,
  quoteChar: '"',
  escapeChar: '"',
  delimiter: ',',
  header: true,
  newline: '\r\n',
  skipEmptyLines: false,
};

/**
 * Formats a given date into a localized string representation.
 *
 * @param dateStr - The database string representation of the date to be formatted.
 * @returns A string representing the formatted date and time in 'cs-CZ' locale.
 */
const getFormattedDateTime = (dateStr: Date) => {
  const date = new Date(dateStr);

  const formattedDate = new Intl.DateTimeFormat('cs-CZ', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);

  return formattedDate;
};

export type DocCheckEditContainerProps = {
  query: Record<string, string | number | boolean | string[]>;
  exportType?: ExportMappingTypeEnum;
  label: string;
  fileName: string;
};

/**
 * A button component for exporting a list of documents to a CSV file.
 *
 * The component accepts the following props:
 * - `query`: The query parameters for the API endpoint to fetch the documents to be exported.
 * - `exportType`: An optional argument to specify the type of export. Supported values are `DEFAULT` (default export), `CHECK` (export of check results), and `DMVS_DTI` (export of DMVS-DTI documents). Default is `DEFAULT`.
 * - `label`: The label of the button.
 * - `fileName`: The name of the exported CSV file.
 *
 * The component fetches the documents from the API endpoint specified by the `query` prop, transforms the data according to the specified `exportType`, and then uses the `Papa.unparse` function to convert the data to a CSV string. The CSV string is then saved as a file using the `URL.createObjectURL` and `Blob` APIs.
 *
 * Note that the component does not handle errors, so it's up to the caller to handle errors and edge cases.
 */
export const DocExport: React.FC<DocCheckEditContainerProps> = ({
  query,
  exportType = ExportMappingType.DEFAULT,
  label,
  fileName,
}) => {
  const formatMessage = useMessage();
  const [queryState, setQueryState] = useState<QueryState | undefined>(undefined);

  useEffect(() => {
    switch (queryState) {
      case 'success': {
        const timeout = window.setTimeout(() => {
          setQueryState(undefined);
        }, 2500);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
  }, [queryState]);

  const getData = useCallback(async (page: number, acc: any[] = []) => {
    const result = await docsList({ query: { ...query, page } });

    if (result.error) {
      return {
        error: result.error,
        data: null,
      };
    }

    const currentData = result.data?.results || [];
    const cachedData = [...acc, ...currentData];

    if (result.data?.next) {
      return getData(page + 1, cachedData);
    }

    return {
      ...result,
      data: {
        ...result.data,
        results: cachedData
      }
    };
  }, [query]);

  const handleExportClick = useCallback(async () => {
    setQueryState('waiting');
    const exportData = await getData(1);

    if (exportData.error || !exportData.data?.results) {
      console.error('Error downloading CSV file', exportData.error);
      setQueryState(undefined);
      return;
    }

    const transformData = exportData.data.results.map((item: Record<string, any>) => {
      const transformItem: Record<string, any> = {};
      Object.entries(MAPPING[exportType]).forEach(([key, labelObject]) => {
        const label = formatMessage(labelObject);
        switch (key) {
          case 'state':
            transformItem[label] = formatDocState(
              formatMessage,
              item[key],
              item.flow,
              item.rejected_by_subjekty,
            );
            break;
          case 'latest_check':
            transformItem[label] = formatMessage(
              checkStateMessages[item[key]?.state as CHECK_STATE],
            );
            break;
          case 'prejimka_type':
            transformItem[label] = formatMessage.lookup(prejimkaTypesMessages, item[key]);
            break;
          case 'created':
            transformItem[label] = getFormattedDateTime(item[key]);
            break;
          case 'history':
            const closeStates = getDocumentStates(HISTORY_CLOSED, ['CLOSED'] as DOC_STATE[]);
            const filteredHistoryItem = item[key].find((item: any) =>
              closeStates.includes(item.state),
            );
            transformItem[label] = filteredHistoryItem?.updated
              ? getFormattedDateTime(filteredHistoryItem.updated)
              : '';
            break;
          case 'creator':
          case 'receiver':
          case 'geodesist':
          case 'editor_manager':
            transformItem[label] = `${item[key]?.name ?? ''} ${item[key]?.surname ?? ''}`.trim();
            break;
          case 'subjekt':
          case 'editor_subjekt':
            transformItem[label] = `${item[key]?.jmeno ?? ''}`.trim();
            break;
          case 'dmvs_source':
            transformItem[label] = formatMessage(
              checkSourceMessages[item[key] ? checkSourceEnum.ISDMVS : checkSourceEnum.ISDTMK],
            );
            break;
          default:
            transformItem[label] = item[key];
            break;
        }
      });
      return transformItem;
    });

    if (!transformData) {
      setQueryState(undefined);
      return null;
    }

    try {
      const csvData = unparse(transformData, csvUnparseConfig);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      // vyvolani stazeni souboru - simulace onclick
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setQueryState('success');
    } catch (error) {
      console.error('Error downloading CSV file', error);
      setQueryState(undefined);
    }
  }, [query, exportType]);

  return (
    <SecondaryBtn queryState={queryState} size="sml" onClick={handleExportClick}>
      {label}
    </SecondaryBtn>
  );
};
